import { useMemo } from 'react';
import styles from './index.module.scss'
import { IMAGE_TYPE, RESOURCE_TYPE } from '@utils/CONST'
import API from '@api/api'
import { useNavigate } from 'react-router-dom';
import { workflowOfSendTask } from '@utils/workflowOfBuildTaskV2';
import { Popover } from 'antd';
import Iconfont from '@utils/Iconfont';

const BottomBtnGroups = (props) => {
    const { showTopBtns, imageType, item, triggerIconType = 'icon-menuOutlined' } = props
    const navigate = useNavigate()
    const currentImageType = item?.imageType || imageType

    const MenuLink = ({ icon, label, onClick }) => (
        <p className={styles.footer_text} onClick={onClick}>
            <Iconfont type={icon} className={styles.Icon} />
            {label}
        </p>
    )

    const MenuSection = ({ title, menus = [] }) => {
        if (!menus.length) return null
        return (
            <div className={styles.MenuSection}>
                {title && <div className={styles.GroupTitle}>{title}</div>}
                <div className={styles.GroupList}>
                    {menus.map((menu, i) => (
                        <MenuLink
                            key={`${menu.label}-${i}`}
                            icon={menu.icon}
                            label={menu.label}
                            onClick={menu.onClick}
                        />
                    ))}
                </div>
            </div>
        )
    }

    const handleClick = async (e, jumpTotype, targetType = "inputImage", extraState) => {
        e.stopPropagation()
        let noWaterRes = await API.commonDownload({ taskId: item?.taskId })
        if (noWaterRes?.code != 0) throw new Error(noWaterRes?.message)
        const image = noWaterRes.data

        const aiEditModes = ['handRepair', 'magicErase', 'image_extraction', 'imageResize', 'imageFiner', 'filllight']
        if (aiEditModes.includes(targetType)) {
            if (targetType === 'image_extraction') {
                window.open(image ? `/home/${targetType}?url=${encodeURIComponent(image)}` : `/home/${targetType}`, '_blank')
            } else if (targetType === 'imageResize') {
                navigate(image ? `/home/aiEdit?menuKey=imageResize&url=${encodeURIComponent(image)}` : `/home/aiEdit?menuKey=imageResize`)
            } else {
                navigate("/home/aiEdit", { state: { menuKey: targetType, picUrl: image } })
            }
            return
        }

        const res = await workflowOfSendTask(jumpTotype, image, targetType, extraState)
        if (!res) return
        const { pathname, options } = res
        navigate(pathname, options)
    }

    const isPersonScene = useMemo(() => {
        const commodityTypes = [
            IMAGE_TYPE.COMMODITY,
            IMAGE_TYPE.COMMODITYRECOLOR,
            IMAGE_TYPE.WATERMARK_REMOVE,
            IMAGE_TYPE.FLATLAY3D,
        ]
        if (commodityTypes.includes(currentImageType)) return false
        if (currentImageType === IMAGE_TYPE.CLOTH_RECOLOR && !item?.clothReColorIsPerson) return false
        return true
    }, [currentImageType, item])

    const leftMenus = useMemo(() => {
        if (!isPersonScene) return []

        const list = [
            { label: 'AI创意生图', icon: 'icon-genimageOutlined', type: IMAGE_TYPE.CREATIVE_IMAGE_GEN },
            { label: '换衣服', icon: 'icon-outfitSmallOutlined', type: IMAGE_TYPE.CLOTHES, resourceType: RESOURCE_TYPE.TRYON_MODEL },
            { label: '换模特', icon: 'icon-aiModelOutlined', type: IMAGE_TYPE.MODEL },
            { label: '换背景', icon: 'icon-aibgOutlined', type: IMAGE_TYPE.BACKGROUND, condition: currentImageType !== IMAGE_TYPE.MODEL },
            { label: '换鞋子', icon: 'icon-shoesOutlined', type: IMAGE_TYPE.SHOES, resourceType: RESOURCE_TYPE.TRYON_SHOES },
            { label: '换眼镜', icon: 'icon-glassesOutlined', type: IMAGE_TYPE.GLASSES_TRYON, resourceType: RESOURCE_TYPE.GLASSES_TRYON },
            { label: '换项链', icon: 'icon-necklaceOutlined', type: IMAGE_TYPE.NECKLACE_TRYON, resourceType: RESOURCE_TYPE.NECKLACE_TRYON, extraState: { silentSelect: true } },
            { label: '换姿势', icon: 'icon-poseFantasyOutlined', type: IMAGE_TYPE.POSE_TRYON },
            { label: '服饰换色', icon: 'icon-recolorOutlined', type: IMAGE_TYPE.CLOTH_RECOLOR },
            { label: '同版型试衣', icon: 'icon-sametryOutlined', type: IMAGE_TYPE.SIM_TRYON, resourceType: RESOURCE_TYPE.SIM_TRYON },
        ]

        return list.filter(m => m.type !== currentImageType && (m.condition === undefined || m.condition))
    }, [isPersonScene, currentImageType])

    const middleMenus = useMemo(() => {
        const list = []
        const isVideoResult = [IMAGE_TYPE.IMAGE_TO_VIDEO, IMAGE_TYPE.MODEL_VIDEO].includes(currentImageType)

        if (!isPersonScene && !isVideoResult) {
            list.push({ label: 'AI创意生图', icon: 'icon-genimageOutlined', type: IMAGE_TYPE.CREATIVE_IMAGE_GEN })
        }

        if (isPersonScene) {
            const personExtras = [
                { label: '换穿搭', icon: 'icon-outfitSmallOutlined', type: IMAGE_TYPE.FUSION_WEAR, resourceType: RESOURCE_TYPE.FUSIONWEAR_MODEL },
                { label: '去种草', icon: 'icon-sProductshowcaseOutlined', type: IMAGE_TYPE.GRASS_PLANT },
                { label: '图生视频', icon: 'icon-tryonVideoOutlined', type: IMAGE_TYPE.IMAGE_TO_VIDEO },
                { label: '动作模仿', icon: 'icon-tryonVideoOutlined', type: IMAGE_TYPE.MODEL_VIDEO },
            ]
            list.push(...personExtras.filter(m => m.type !== currentImageType))
        }

        if (currentImageType !== IMAGE_TYPE.COMMODITYRECOLOR) {
            list.push({ label: '商品换色', icon: 'icon-recolorOutlined', type: IMAGE_TYPE.COMMODITYRECOLOR })
        }

        if ([IMAGE_TYPE.COMMODITYRECOLOR, IMAGE_TYPE.WATERMARK_REMOVE].includes(currentImageType)) {
            list.push({ label: '商品换背', icon: 'icon-commidityOutlined', type: IMAGE_TYPE.COMMODITY })
        }

        const commodityRelatedTypes = [IMAGE_TYPE.COMMODITY, IMAGE_TYPE.COMMODITYRECOLOR, IMAGE_TYPE.WATERMARK_REMOVE, IMAGE_TYPE.FLATLAY3D]
        if (commodityRelatedTypes.includes(currentImageType)) {
            if (!list.find(m => m.type === IMAGE_TYPE.IMAGE_TO_VIDEO) && currentImageType !== IMAGE_TYPE.IMAGE_TO_VIDEO) {
                list.push({ label: '图生视频', icon: 'icon-tryonVideoOutlined', type: IMAGE_TYPE.IMAGE_TO_VIDEO })
            }
            if (!list.find(m => m.type === IMAGE_TYPE.CLOTH_RECOLOR) && currentImageType !== IMAGE_TYPE.CLOTH_RECOLOR) {
                list.push({ label: '服饰换色', icon: 'icon-recolorOutlined', type: IMAGE_TYPE.CLOTH_RECOLOR })
            }
        }

        return list
    }, [isPersonScene, currentImageType])

    const rightMenus = useMemo(() => {
        const list = []
        if (![IMAGE_TYPE.COMMODITY, IMAGE_TYPE.COMMODITYRECOLOR, IMAGE_TYPE.FLATLAY3D].includes(currentImageType)) {
            list.push({ label: '修手', icon: 'icon-handRepairOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'handRepair' })
        }

        list.push(
            { label: '抠图', icon: 'icon-mattingOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'image_extraction' },
            { label: '改尺寸', icon: 'icon-picResizeOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'imageResize' },
            { label: 'AI超清', icon: 'icon-hasHDOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'imageFiner' },
            { label: '去杂物', icon: 'icon-magicRemoveOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'magicErase' },
            { label: '修光影', icon: 'icon-lightIconOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'filllight' },
        )
        return list
    }, [currentImageType])

    const bindClick = (menus, key = 'resourceType') => (
        menus.map((menu) => ({
            ...menu,
            onClick: (e) => handleClick(e, menu.type, menu[key], menu.extraState),
        }))
    )

    const leftPrimary = useMemo(() => leftMenus.filter(menu => menu.type === IMAGE_TYPE.CREATIVE_IMAGE_GEN), [leftMenus])
    const leftEditMenus = useMemo(() => leftMenus.filter(menu => menu.type !== IMAGE_TYPE.CREATIVE_IMAGE_GEN), [leftMenus])
    const middleVideoMenus = useMemo(() => middleMenus.filter(menu => [IMAGE_TYPE.IMAGE_TO_VIDEO, IMAGE_TYPE.MODEL_VIDEO].includes(menu.type)), [middleMenus])
    const middleOtherMenus = useMemo(() => middleMenus.filter(menu => ![IMAGE_TYPE.IMAGE_TO_VIDEO, IMAGE_TYPE.MODEL_VIDEO].includes(menu.type)), [middleMenus])

    const popover = (
        <div className={styles.popoverContent}>
            {leftMenus.length > 0 && (
                <div className={styles.Column}>
                    <MenuSection menus={bindClick(leftPrimary)} />
                    <MenuSection title={leftEditMenus.length > 0 ? '修改图片' : ''} menus={bindClick(leftEditMenus)} />
                </div>
            )}

            {middleMenus.length > 0 && (
                <div className={styles.Column}>
                    <MenuSection menus={bindClick(middleOtherMenus)} />
                    <MenuSection title={middleVideoMenus.length > 0 ? '视频生成' : ''} menus={bindClick(middleVideoMenus)} />
                </div>
            )}

            {rightMenus.length > 0 && (
                <div className={styles.Column}>
                    <MenuSection title="AI编辑" menus={bindClick(rightMenus, 'targetType')} />
                </div>
            )}
        </div>
    )

    const MoreOutMenuContent = (
        <div className={styles.MoreOutMenuContent}>
            <Popover placement="top" content={popover} overlayClassName={styles.BottomMenuPopover}>
                <span className={`${styles.spanItem} ${styles.spanItem2}`}>
                    <Iconfont
                        type={triggerIconType}
                        className={`${styles.Icon} ${triggerIconType === 'icon-Material-EditOutlined' ? styles.IconNoRotate : ''}`}
                    />
                </span>
            </Popover>
        </div>
    )

    return (
        (showTopBtns && currentImageType != IMAGE_TYPE.MODEL_VIDEO && currentImageType != IMAGE_TYPE.IMAGE_TO_VIDEO) &&
        (<div className={styles.BottomBtnGroups}>
            {MoreOutMenuContent}
        </div>)
    )
}

export default BottomBtnGroups
